import React, { useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

import PropTypes from 'prop-types';

import { useRouter } from 'next/router';

import { IconButton } from '@iris/react-components';

import { useDevice } from '@bonnet/next/device';

import { useFeatures } from '@atc/features';

import { sendClick } from 'reaxl-analytics';

import { shoppingAssistantIconClick } from '@/analytics/shoppingAssistantEvents';

import listingsAgentDuck from '@/ducks/listingsAgentDuck';

import AnimatedSparkles from '@/components/AnimatedSparkles';

import ShoppingAssistantPopover from './ShoppingAssistantPopover';

function ShoppingAssistantContainer({
    handleAssistantClose,
    headingText,
    setShowAssistant,
    showAssistant,
    showChatDialog = false,
    suggestedActions,
}) {
    const dispatch = useDispatch();
    const router = useRouter();
    const device = useDevice();
    const isXs = device?.is?.xs;

    const {
        shopping_assistant: [, { nudge_delay: nudgeDelay }],
    } = useFeatures(['shopping_assistant']);

    // Track URL changes to clear chat messages on navigation
    const currentUrl = router.asPath;
    const prevUrlRef = useRef(currentUrl);

    // Clear chat messages when URL changes (handles both page nav and listing changes)
    useEffect(() => {
        if (prevUrlRef.current !== currentUrl) {
            dispatch(listingsAgentDuck.creators.clearMessages());
            prevUrlRef.current = currentUrl;
        }
    }, [currentUrl, dispatch]);

    const handleAssistantClick = (event) => {
        sendClick(shoppingAssistantIconClick, event, {});
        setShowAssistant((prev) => !prev);
    };

    const triggerButton = (
        <IconButton
            aria-label="Open Shopping Assistant"
            icon={<AnimatedSparkles delay={nudgeDelay} />}
            id="shopping-assistant-entry"
            inverse
            onClick={handleAssistantClick}
            size="md"
            style={{ bottom: isXs ? 70 : 100 }}
        />
    );

    return (
        <div className="shopping-assistant">
            <ShoppingAssistantPopover
                containerElement={{ offsetWidth: 400 }}
                handleAssistantClose={handleAssistantClose}
                headingText={headingText}
                show={showAssistant}
                showChatDialog={showChatDialog}
                suggestedActions={suggestedActions}
                trigger={triggerButton}
            />
        </div>
    );
}

ShoppingAssistantContainer.propTypes = {
    /**
     * Function to handle closing the assistant
     */
    handleAssistantClose: PropTypes.func.isRequired,
    /**
     * The text to be displayed in the header of the popover
     */
    headingText: PropTypes.string,
    /**
     * Function to toggle the assistant visibility state
     */
    setShowAssistant: PropTypes.func.isRequired,
    /**
     * If true, the shopping assistant will be shown
     */
    showAssistant: PropTypes.bool.isRequired,
    /**
     * If true, the chat dialog will be displayed
     * @default false
     */
    showChatDialog: PropTypes.bool,
    /**
     * A group of suggested actions to be rendered in the popover
     */
    suggestedActions: PropTypes.arrayOf(PropTypes.shape({
        action: PropTypes.string,
        label: PropTypes.string,
    })),
};

export default ShoppingAssistantContainer;
